import { useEffect, useState } from "react";
import { getProof, isNotFound } from "../../api/client";
import type { Proof } from "../../api/types";
import { Button, ExternalButton } from "../../components/Button";
import { Callout } from "../../components/Callout";
import { Rows } from "../../components/Rows";
import { Sheet } from "../../components/Sheet";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import { useConfig } from "../../hooks/useConfig";
import { formatTokenAmount, shortAddress } from "../../lib/format";
import { ROBINHOOD_CHAIN_ID, ROBINHOOD_EXPLORER_URL, ROBINHOOD_RPC_URL, txUrl } from "../../wallets/chain";
import { readIsClaimed, sendClaim } from "../../wallets/distributor";
import { WalletMissingError, walletErrorMessage } from "../../wallets/errors";
import { ensureRobinhoodChain, hasEthereum } from "../../wallets/metamask";
import { StepShell } from "./StepShell";
import type { AirdropFlow } from "./useAirdropFlow";

const CLOSED_COPY = "Claims open once registration has closed and the distributor is funded.";

export function StepClaim({ flow }: { flow: AirdropFlow }) {
  const { config } = useConfig();
  const [proof, setProof] = useState<Proof | null>(null);
  const [noLeaf, setNoLeaf] = useState(false);
  const [claimed, setClaimed] = useState<boolean | null>(null);
  const [txHash, setTxHash] = useState<string | null>(null);

  const claiming = config?.phase === "claim";
  const distributor = config?.distributor ?? null;
  const chainId = config?.chainId ?? ROBINHOOD_CHAIN_ID;
  const rpcUrl = config?.rpcUrl ?? ROBINHOOD_RPC_URL;
  const explorerUrl = config?.explorerUrl ?? ROBINHOOD_EXPLORER_URL;
  const target = flow.eligibility?.registration?.evmAddress ?? flow.evmAddress;

  // Look up the leaf for the registered address once claiming is open.
  useEffect(() => {
    if (!claiming || !target) {
      setProof(null);
      setNoLeaf(false);
      return;
    }
    let live = true;
    setNoLeaf(false);
    getProof(target)
      .then((next) => {
        if (live) setProof(next);
      })
      .catch((error: unknown) => {
        if (!live) return;
        setProof(null);
        if (isNotFound(error)) setNoLeaf(true);
      });
    return () => {
      live = false;
    };
  }, [claiming, target]);

  // Ask the distributor whether this leaf was already used. Stays quiet on failure.
  useEffect(() => {
    if (!proof || !distributor) {
      setClaimed(null);
      return;
    }
    let live = true;
    void readIsClaimed(distributor, proof.index, chainId, rpcUrl, explorerUrl).then((value) => {
      if (live) setClaimed(value);
    });
    return () => {
      live = false;
    };
  }, [proof, distributor, chainId, rpcUrl, explorerUrl]);

  const claim = useAsyncAction(async () => {
    if (!proof || !distributor) throw new Error("Nothing to claim yet.");
    if (!hasEthereum()) throw new WalletMissingError("metamask");
    await ensureRobinhoodChain(chainId, rpcUrl, explorerUrl);
    const hash = await sendClaim({
      distributor,
      index: proof.index,
      account: proof.account,
      amount: proof.amount,
      proof: proof.proof,
      chainId,
      rpcUrl,
      explorerUrl,
    });
    setTxHash(hash);
    setClaimed(true);
    return hash;
  }, (error) => {
    if (error instanceof WalletMissingError) {
      flow.setMissingWallet("metamask");
      return "";
    }
    return walletErrorMessage(error, "The claim transaction failed.");
  });

  const symbol = config?.token.symbol ?? "TISM";
  const decimals = config?.token.decimals ?? 18;

  return (
    <StepShell number={3} title="Claim $TISM" active={claiming && proof !== null}>
      {!claiming ? (
        <Callout tone="neutral" role="status">
          {CLOSED_COPY}
        </Callout>
      ) : !target ? (
        <p className="body">Register an EVM address first. The claim is sent to that address.</p>
      ) : noLeaf ? (
        <Callout tone="neutral" role="status">
          No claim was found for {shortAddress(target, 8, 6)}. Only addresses registered before
          registration closed are in the tree.
        </Callout>
      ) : !proof ? (
        <p className="body">Looking up your claim.</p>
      ) : (
        <>
          <div className="card">
            <Rows
              items={[
                {
                  key: "to",
                  label: "Sent to",
                  value: <span className="mono">{proof.account}</span>,
                  stack: true,
                },
                {
                  key: "amount",
                  label: "Amount",
                  value: `${formatTokenAmount(proof.amount, decimals)} ${symbol}`,
                },
                { key: "index", label: "Leaf index", value: `#${proof.index}` },
              ]}
            />
          </div>

          {claimed === true ? (
            <Callout role="status">This allocation has already been claimed.</Callout>
          ) : (
            <Button
              onClick={() => void claim.run()}
              pending={claim.pending}
              disabled={!distributor}
            >
              Claim {symbol}
            </Button>
          )}

          {!distributor ? (
            <p className="tiny">The distributor address is not published yet.</p>
          ) : null}

          {claim.error ? (
            <Callout tone="error" role="alert">
              {claim.error}
            </Callout>
          ) : null}
        </>
      )}

      <Sheet
        open={txHash !== null}
        title="Claim submitted"
        onClose={() => setTxHash(null)}
      >
        <div className="stack stack-4">
          <p className="body">
            Your claim was sent to Robinhood Chain. It lands once the transaction confirms.
          </p>
          <p className="mono">{txHash}</p>
          {txHash ? (
            <ExternalButton href={txUrl(explorerUrl, txHash)} block>
              View on Blockscout
            </ExternalButton>
          ) : null}
        </div>
      </Sheet>
    </StepShell>
  );
}
